const BASE_URL = '/api';

export class ApiError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = {};

  if (options.body) {
    headers['Content-Type'] = 'application/json';
  }

  const token = localStorage.getItem('token');
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${BASE_URL}${path}`, {
    ...options,
    headers: {
      ...headers,
      ...((options.headers as Record<string, string>) ?? {}),
    },
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new ApiError(body.error || `Request failed with status ${response.status}`, response.status);
  }

  return response.json() as Promise<T>;
}

export interface AuthResponse {
  token: string;
  user: {
    id: string;
    tenantId: string;
    email: string;
    name: string;
    role: string;
  };
  tenant?: {
    id: string;
    name: string;
    slug: string;
    plan: string;
  };
}

export interface MeResponse {
  user: {
    id: string;
    tenantId: string;
    email: string;
    name: string;
    role: string;
    phone: string | null;
    tenantType: string;
    tenant: {
      id: string;
      name: string;
      plan: string;
      tenantType: string;
    };
  };
}

export function register(data: {
  email: string;
  password: string;
  name: string;
  organizationName: string;
  tenantType?: 'manufacturer' | 'reseller';
}) {
  return request<AuthResponse>('/auth/register', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export function login(data: { email: string; password: string }) {
  return request<AuthResponse>('/auth/login', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export function getMe() {
  return request<MeResponse>('/auth/me');
}

export function updateProfile(data: { name?: string; email?: string; phone?: string }) {
  return request<MeResponse>('/auth/me', {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Catalogs
export interface Catalog {
  id: string;
  name: string;
  description: string | null;
  active: boolean;
  sortOrder: number;
  isSubscribedCopy: boolean;
  sourcePublishedCatalogId: string | null;
  resellerDiscount: string | null;
  createdAt: string;
  updatedAt: string;
}

export function listCatalogs() {
  return request<{ catalogs: Catalog[] }>('/catalogs');
}

export function getCatalog(id: string) {
  return request<{ catalog: Catalog }>(`/catalogs/${id}`);
}

export function createCatalog(data: { name: string; description?: string }) {
  return request<{ catalog: Catalog }>('/catalogs', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export function updateCatalog(id: string, data: Partial<Catalog>) {
  return request<{ catalog: Catalog }>(`/catalogs/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export function deleteCatalog(id: string) {
  return request<{ message: string }>(`/catalogs/${id}`, { method: 'DELETE' });
}

// Products
export interface Product {
  id: string;
  catalogId: string;
  groupName: string | null;
  partNumber: string;
  description: string | null;
  msrp: string;
  applyProjectDiscounts: boolean;
  sortOrder: number;
}

export function listProducts(params?: {
  catalogId?: string;
  search?: string;
  groupName?: string;
}) {
  const query = new URLSearchParams();
  if (params?.catalogId) query.set('catalog_id', params.catalogId);
  if (params?.search) query.set('search', params.search);
  if (params?.groupName) query.set('group_name', params.groupName);
  const qs = query.toString();
  return request<{ products: Product[] }>(`/products${qs ? `?${qs}` : ''}`);
}

export function createProduct(data: {
  catalogId: string;
  groupName?: string;
  partNumber: string;
  description?: string;
  msrp: number;
  applyProjectDiscounts?: boolean;
}) {
  return request<{ product: Product }>('/products', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export function updateProduct(
  id: string,
  data: Partial<Omit<Product, 'msrp'>> & { msrp?: number },
) {
  return request<{ product: Product }>(`/products/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export function deleteProduct(id: string) {
  return request<{ message: string }>(`/products/${id}`, { method: 'DELETE' });
}

export function batchDeleteProducts(ids: string[]) {
  return request<{ count: number }>('/products/batch-delete', {
    method: 'POST',
    body: JSON.stringify({ ids }),
  });
}

export function bulkCreateProducts(data: {
  catalogId: string;
  products: Array<{
    groupName?: string;
    partNumber: string;
    description?: string;
    msrp: number;
    applyProjectDiscounts?: boolean;
  }>;
}) {
  return request<{ count: number }>('/products/bulk', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export function getProductGroups(catalogId?: string) {
  const qs = catalogId ? `?catalog_id=${catalogId}` : '';
  return request<{ groups: string[] }>(`/products/groups${qs}`);
}

export function searchProducts(params: {
  catalogIds?: string[];
  search?: string;
  groupName?: string;
  limit?: number;
}) {
  const query = new URLSearchParams();
  if (params.catalogIds?.length) query.set('catalog_ids', params.catalogIds.join(','));
  if (params.search) query.set('search', params.search);
  if (params.groupName) query.set('group_name', params.groupName);
  if (params.limit) query.set('limit', params.limit.toString());
  return request<{ products: (Product & { catalogName: string })[] }>(
    `/products/search?${query}`,
  );
}

// Resellers
export interface Reseller {
  id: string;
  name: string;
  defaultDiscount: string;
  active: boolean;
  dateAdded: string;
  lastQuoteDate: string | null;
  quoteCount: number;
}

export interface ResellerWithQuoteCount extends Reseller {
  liveQuoteCount: number;
}

export interface ResellerQuote {
  id: string;
  displayId: string | null;
  projectName: string | null;
  status: string;
  createdAt: string;
  updatedAt: string;
}

export function listResellers(params?: { active?: boolean; search?: string }) {
  const query = new URLSearchParams();
  if (params?.active !== undefined) query.set('active', String(params.active));
  if (params?.search) query.set('search', params.search);
  const qs = query.toString();
  return request<{ resellers: Reseller[] }>(`/resellers${qs ? `?${qs}` : ''}`);
}

export function getReseller(id: string) {
  return request<{ reseller: ResellerWithQuoteCount }>(`/resellers/${id}`);
}

export function createReseller(data: { name: string; defaultDiscount: number }) {
  return request<{ reseller: Reseller }>('/resellers', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export function updateReseller(
  id: string,
  data: { name?: string; defaultDiscount?: number; active?: boolean },
) {
  return request<{ reseller: Reseller }>(`/resellers/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export function deleteReseller(id: string) {
  return request<{ message: string }>(`/resellers/${id}`, { method: 'DELETE' });
}

export function getResellerQuotes(
  id: string,
  params?: { status?: string; limit?: number },
) {
  const query = new URLSearchParams();
  if (params?.status) query.set('status', params.status);
  if (params?.limit) query.set('limit', String(params.limit));
  const qs = query.toString();
  return request<{ quotes: ResellerQuote[] }>(`/resellers/${id}/quotes${qs ? `?${qs}` : ''}`);
}

// Reseller Contacts
export interface ResellerContact {
  id: string;
  resellerId: string;
  name: string;
  email: string | null;
  phone: string | null;
  createdAt: string;
}

export function listResellerContacts(resellerId: string) {
  return request<{ contacts: ResellerContact[] }>(`/resellers/${resellerId}/contacts`);
}

export function createResellerContact(
  resellerId: string,
  data: { name: string; email?: string; phone?: string },
) {
  return request<{ contact: ResellerContact }>(`/resellers/${resellerId}/contacts`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export function updateResellerContact(
  resellerId: string,
  contactId: string,
  data: { name?: string; email?: string | null; phone?: string | null },
) {
  return request<{ contact: ResellerContact }>(
    `/resellers/${resellerId}/contacts/${contactId}`,
    {
      method: 'PUT',
      body: JSON.stringify(data),
    },
  );
}

export function deleteResellerContact(resellerId: string, contactId: string) {
  return request<{ message: string }>(`/resellers/${resellerId}/contacts/${contactId}`, {
    method: 'DELETE',
  });
}

// Quotes
export interface QuoteListItem {
  id: string;
  displayId: string | null;
  projectName: string | null;
  resellerName: string | null;
  resellerId: string | null;
  resellerDiscount: string;
  projectDiscount: string;
  status: string;
  archived: boolean;
  validUntil: string | null;
  quotedBy: string | null;
  lineItemCount: number;
  totalMsrp: number;
  createdAt: string;
  updatedAt: string;
}

export interface QuoteLineItem {
  id: string;
  productId: string | null;
  isDescriptionRow: boolean;
  descriptionText: string | null;
  quantity: number;
  unitPrice: string | null;
  projectDiscountOverride: string | null;
  marginOverride: string | null;
  sellPriceOverride: string | null;
  discountSnapshot: string | null;
  sortOrder: number;
  partNumber?: string;
  description?: string;
  groupName?: string;
  catalogName?: string;
  applyProjectDiscounts?: boolean;
}

export interface QuoteDetail {
  id: string;
  displayId: string | null;
  projectName: string | null;
  resellerId: string | null;
  resellerName: string | null;
  resellerDiscount: string;
  projectDiscount: string;
  resellerContact: string | null;
  resellerContactId: string | null;
  resellerContactEmail: string | null;
  resellerContactPhone: string | null;
  quotedBy: string | null;
  quotedByTitle: string | null;
  quotedByEmail: string | null;
  quotedByPhone: string | null;
  status: string;
  visibility: string;
  boilerplateText: string | null;
  boilerplateOverride: boolean;
  quoteNotes: string | null;
  targetMargin: string | null;
  notes: string | null;
  archived: boolean;
  validUntil: string | null;
  lineItems: QuoteLineItem[];
  totalMsrp: number;
  totalReseller: number;
  createdAt: string;
  updatedAt: string;
}

export function listQuotes(params?: {
  resellerId?: string;
  search?: string;
  status?: string;
  archived?: boolean;
}) {
  const query = new URLSearchParams();
  if (params?.resellerId) query.set('reseller_id', params.resellerId);
  if (params?.search) query.set('search', params.search);
  if (params?.status) query.set('status', params.status);
  if (params?.archived) query.set('archived', 'true');
  const qs = query.toString();
  return request<{ quotes: QuoteListItem[] }>(`/quotes${qs ? `?${qs}` : ''}`);
}

export function getQuote(id: string) {
  return request<{ quote: QuoteDetail }>(`/quotes/${id}`);
}

export function createQuote(data: {
  resellerId?: string;
  projectName?: string;
  resellerDiscount?: number;
  projectDiscount?: number;
  resellerContact?: string;
  quotedBy?: string;
  quotedByEmail?: string;
  quotedByPhone?: string;
  notes?: string;
}) {
  return request<{ quote: QuoteDetail }>('/quotes', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export function updateQuote(id: string, data: Record<string, unknown>) {
  return request<{ quote: QuoteDetail }>(`/quotes/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export function deleteQuote(id: string) {
  return request<{ message: string }>(`/quotes/${id}`, { method: 'DELETE' });
}

export function duplicateQuote(id: string) {
  return request<{ quote: QuoteDetail }>(`/quotes/${id}/duplicate`, { method: 'POST' });
}

export function addLineItems(
  quoteId: string,
  items: Array<{
    productId?: string;
    isDescriptionRow?: boolean;
    descriptionText?: string;
    quantity?: number;
    unitPrice?: number;
    projectDiscountOverride?: number | null;
  }>,
) {
  return request<{ lineItems: QuoteLineItem[] }>(`/quotes/${quoteId}/line-items`, {
    method: 'POST',
    body: JSON.stringify({ items }),
  });
}

export function updateLineItem(
  quoteId: string,
  lineItemId: string,
  data: {
    quantity?: number;
    projectDiscountOverride?: number | null;
    marginOverride?: number | null;
    discountSnapshot?: number;
    descriptionText?: string;
  },
) {
  return request<{ lineItem: QuoteLineItem }>(`/quotes/${quoteId}/line-items/${lineItemId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export function deleteLineItem(quoteId: string, lineItemId: string) {
  return request<{ message: string }>(`/quotes/${quoteId}/line-items/${lineItemId}`, {
    method: 'DELETE',
  });
}

export function reorderLineItems(quoteId: string, itemIds: string[]) {
  return request<{ message: string }>(`/quotes/${quoteId}/line-items/reorder`, {
    method: 'PUT',
    body: JSON.stringify({ itemIds }),
  });
}

export function refreshQuotePrices(quoteId: string) {
  return request<{
    updated: number;
    changes: Array<{ partNumber: string; oldPrice: number; newPrice: number }>;
  }>(`/quotes/${quoteId}/refresh-prices`, {
    method: 'POST',
  });
}

// PDF export uses raw fetch for blob download — see QuoteBuilderPage

// Tenant Settings
export interface TenantSettings {
  id: string;
  name: string;
  companyName: string | null;
  companyAddress: string | null;
  companyPhone: string | null;
  companyEmail: string | null;
  companyWebsite: string | null;
  logoUrl: string | null;
  quotePrefix: string;
  defaultCurrency: string;
  quoteBoilerplate: string | null;
  boilerplateEditable: boolean;
  defaultValidityDays: number | null;
  quoteDocumentTitle: string | null;
  tenantType: string | null;
}

export function getTenantSettings() {
  return request<{ tenant: TenantSettings }>('/tenant');
}

export function updateTenantSettings(data: Partial<TenantSettings>) {
  return request<{ tenant: TenantSettings }>('/tenant', {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function uploadTenantLogo(file: File): Promise<{ logoUrl: string }> {
  const token = localStorage.getItem('token');
  const formData = new FormData();
  formData.append('logo', file);
  const response = await fetch('/api/tenant/logo', {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({ message: 'Upload failed' }));
    throw new ApiError(err.error || err.message || 'Upload failed', response.status);
  }
  return response.json();
}

export async function deleteTenantLogo(): Promise<void> {
  await request<{ message: string }>('/tenant/logo', { method: 'DELETE' });
}

// Dashboard
export interface DashboardStats {
  quoteCount: number;
  draftCount: number;
  sentCount: number;
  readyCount: number;
  finalCount: number;
  archivedCount: number;
  resellerCount: number;
  productCount: number;
  catalogCount: number;
  recentQuotes: Array<{
    id: string;
    displayId: string | null;
    projectName: string | null;
    resellerName: string | null;
    status: string;
    updatedAt: string;
  }>;
  incomingStats: { total: number; newCount: number };
  recentIncoming: Array<{
    id: string;
    displayId: string | null;
    projectReference: string | null;
    projectName: string | null;
    sourceTenantName: string;
    status: string;
    sentAt: string;
  }>;
  sentStats: { total: number; totalViews: number; totalDownloads: number };
  recentSent: Array<{
    id: string;
    displayId: string | null;
    projectReference: string | null;
    projectName: string | null;
    recipientTenantName: string;
    status: string;
    viewCount: number;
    downloadCount: number;
    sentAt: string;
  }>;
}

export function getDashboardStats() {
  return request<DashboardStats>('/dashboard/stats');
}

// Quote Import/Export
export function importQuote(data: unknown) {
  return request<any>('/quotes/import', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// Users
export interface UserListItem {
  id: string;
  name: string;
  email: string;
  role: string;
  phone: string | null;
  createdAt: string;
}

export function listUsers() {
  return request<{ users: UserListItem[] }>('/users');
}

export function createUser(data: { name: string; email: string; password: string; role: string }) {
  return request<{ user: UserListItem }>('/users', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export function updateUserRole(userId: string, role: string) {
  return request<{ id: string; role: string }>(`/users/${userId}/role`, {
    method: 'PUT',
    body: JSON.stringify({ role }),
  });
}

export function deleteUser(userId: string) {
  return request<{ message: string }>(`/users/${userId}`, {
    method: 'DELETE',
  });
}

// Ideas & Feature Requests
export interface IdeaListItem {
  id: string;
  tenantId: string;
  userId: string;
  title: string;
  description: string;
  category: string;
  status: string;
  adminResponse: string | null;
  ratingCount: number;
  ratingAverage: number;
  mergedIntoId: string | null;
  createdAt: string;
  updatedAt: string;
  authorName: string;
  commentCount: number;
  myRating: number | null;
}

export interface IdeaComment {
  id: string;
  userId: string;
  text: string;
  isSystem: boolean;
  createdAt: string;
  userName: string;
}

export interface IdeaDetail {
  id: string;
  tenantId: string;
  userId: string;
  title: string;
  description: string;
  category: string;
  status: string;
  adminResponse: string | null;
  ratingCount: number;
  ratingAverage: number;
  mergedIntoId: string | null;
  createdAt: string;
  updatedAt: string;
  authorName: string;
  comments: IdeaComment[];
  ratingDistribution: Record<number, number>;
  myRating: number | null;
  mergedIdeas: { id: string; title: string }[];
}

export function listIdeas(params?: {
  status?: string;
  category?: string;
  sort?: string;
  search?: string;
  includeMerged?: boolean;
}) {
  const query = new URLSearchParams();
  if (params?.status) query.set('status', params.status);
  if (params?.category) query.set('category', params.category);
  if (params?.sort) query.set('sort', params.sort);
  if (params?.search) query.set('search', params.search);
  if (params?.includeMerged) query.set('include_merged', 'true');
  const qs = query.toString();
  return request<{ ideas: IdeaListItem[] }>(`/ideas${qs ? `?${qs}` : ''}`);
}

export function getIdeaDetail(id: string) {
  return request<{ idea: IdeaDetail }>(`/ideas/${id}`);
}

export function createIdea(data: { title: string; description: string; category: string }) {
  return request<{ idea: IdeaListItem }>('/ideas', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export function rateIdea(ideaId: string, rating: number) {
  return request<{ message: string }>(`/ideas/${ideaId}/rate`, {
    method: 'POST',
    body: JSON.stringify({ rating }),
  });
}

export function addIdeaComment(ideaId: string, text: string) {
  return request<{ comment: IdeaComment }>(`/ideas/${ideaId}/comments`, {
    method: 'POST',
    body: JSON.stringify({ text }),
  });
}

export function updateIdea(
  ideaId: string,
  data: { status?: string; adminResponse?: string; category?: string },
) {
  return request<{ idea: IdeaListItem }>(`/ideas/${ideaId}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export function mergeIdea(targetIdeaId: string, sourceIdeaId: string) {
  return request<{ message: string }>(`/ideas/${targetIdeaId}/merge`, {
    method: 'POST',
    body: JSON.stringify({ sourceIdeaId }),
  });
}

// Bug Reports
export interface BugListItem {
  id: string;
  tenantId: string;
  userId: string;
  title: string;
  stepsToReproduce: string;
  expectedBehavior: string;
  actualBehavior: string;
  severity: string;
  affectedArea: string;
  status: string;
  adminResponse: string | null;
  duplicateOfId: string | null;
  createdAt: string;
  updatedAt: string;
  authorName: string;
  commentCount: number;
}

export interface BugComment {
  id: string;
  userId: string;
  text: string;
  isSystem: boolean;
  createdAt: string;
  userName: string;
}

export interface BugDetail extends BugListItem {
  comments: BugComment[];
  duplicateBugs: { id: string; title: string }[];
}

export function listBugs(params?: {
  severity?: string;
  status?: string;
  affectedArea?: string;
  sort?: string;
  search?: string;
  includeClosed?: boolean;
}) {
  const query = new URLSearchParams();
  if (params?.severity) query.set('severity', params.severity);
  if (params?.status) query.set('status', params.status);
  if (params?.affectedArea) query.set('affectedArea', params.affectedArea);
  if (params?.sort) query.set('sort', params.sort);
  if (params?.search) query.set('search', params.search);
  if (params?.includeClosed) query.set('include_closed', 'true');
  const qs = query.toString();
  return request<{ bugs: BugListItem[] }>(`/bugs${qs ? `?${qs}` : ''}`);
}

export function getBugDetail(id: string) {
  return request<{ bug: BugDetail }>(`/bugs/${id}`);
}

export function createBug(data: {
  title: string;
  stepsToReproduce: string;
  expectedBehavior: string;
  actualBehavior: string;
  severity: string;
  affectedArea: string;
}) {
  return request<{ bug: BugListItem }>('/bugs', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export function addBugComment(bugId: string, text: string) {
  return request<{ comment: BugComment }>(`/bugs/${bugId}/comments`, {
    method: 'POST',
    body: JSON.stringify({ text }),
  });
}

export function updateBug(
  bugId: string,
  data: { status?: string; adminResponse?: string; severity?: string; affectedArea?: string },
) {
  return request<{ bug: BugListItem }>(`/bugs/${bugId}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export function markBugDuplicate(bugId: string, duplicateOfId: string) {
  return request<{ message: string }>(`/bugs/${bugId}/duplicate`, {
    method: 'POST',
    body: JSON.stringify({ duplicateOfId }),
  });
}

// Marketplace
export interface PublishedCatalog {
  id: string;
  catalogId: string;
  catalogName: string;
  catalogDescription: string | null;
  publisherName: string;
  tenantId: string;
  marketplaceStatus: string;
  rejectionReason: string | null;
  version: number;
  publishedAt: string;
  subscriberCount?: number;
  productCount?: number;
  isSubscribed?: boolean;
  tenantName?: string;
  marketplaceRequestedAt?: string | null;
  ownerName?: string | null;
  ownerEmail?: string | null;
}

export interface CatalogSubscription {
  id: string;
  publishedCatalogId: string;
  localCatalogId: string;
  syncedVersion: number;
  subscribedAt: string;
  lastSyncedAt: string;
  catalogName?: string;
  publisherName?: string;
}

export function publishCatalogToMarketplace(catalogId: string, publisherName?: string) {
  return request<{ published: PublishedCatalog }>(`/marketplace/publish/${catalogId}`, {
    method: 'POST',
    body: JSON.stringify({ publisherName }),
  });
}

export function requestMarketplaceListing(catalogId: string) {
  return request<{ published: PublishedCatalog }>(`/marketplace/request-listing/${catalogId}`, {
    method: 'POST',
  });
}

export function unpublishCatalogFromMarketplace(catalogId: string) {
  return request<{ message: string }>(`/marketplace/unpublish/${catalogId}`, {
    method: 'DELETE',
  });
}

export function getMyPublished() {
  return request<{ published: PublishedCatalog[] }>('/marketplace/my-published');
}

export function getPublishStatus(catalogId: string) {
  return request<{ status: PublishedCatalog | null }>(`/marketplace/publish-status/${catalogId}`);
}

export function browseMarketplace() {
  return request<{ catalogs: PublishedCatalog[] }>('/marketplace/browse');
}

export function subscribeToCatalog(publishedCatalogId: string) {
  return request<{ subscription: CatalogSubscription; localCatalog: Catalog; productCount: number }>(
    `/marketplace/subscribe/${publishedCatalogId}`,
    { method: 'POST' },
  );
}

export function unsubscribeFromCatalog(subscriptionId: string) {
  return request<{ message: string }>(`/marketplace/unsubscribe/${subscriptionId}`, {
    method: 'DELETE',
  });
}

export function listMySubscriptions() {
  return request<{ subscriptions: CatalogSubscription[] }>('/marketplace/subscriptions');
}

export function listPendingApprovals() {
  return request<{ pending: PublishedCatalog[] }>('/marketplace/admin/pending');
}

export function approveMarketplaceListing(publishedCatalogId: string) {
  return request<{ published: PublishedCatalog }>(
    `/marketplace/admin/approve/${publishedCatalogId}`,
    { method: 'POST' },
  );
}

export function rejectMarketplaceListing(publishedCatalogId: string, reason: string) {
  return request<{ published: PublishedCatalog }>(
    `/marketplace/admin/reject/${publishedCatalogId}`,
    { method: 'POST', body: JSON.stringify({ reason }) },
  );
}

// Sharing — Cross-Tenant Quote Delivery & Share Links
export interface ConnectedReseller {
  id: string;
  name: string;
  companyName: string | null;
}

export interface IncomingQuoteListItem {
  id: string;
  quoteId: string;
  displayId: string | null;
  projectName: string | null;
  projectReference: string | null;
  notes: string | null;
  sourceTenantName: string;
  status: string;
  sentAt: string;
  viewCount: number;
  downloadCount: number;
  lineItemCount: number;
  totalMsrp: number;
  validUntil: string | null;
}

export interface IncomingQuoteDetail {
  sharedQuote: {
    id: string;
    quoteId: string;
    projectReference: string | null;
    notes: string | null;
    status: string;
    viewCount: number;
    downloadCount: number;
    sentAt: string;
  };
  quote: QuoteDetail;
  sourceTenant: {
    name: string;
    companyName: string | null;
    companyAddress: string | null;
    companyPhone: string | null;
    companyEmail: string | null;
    logoUrl: string | null;
  };
}

export interface ShareLink {
  id: string;
  shareToken: string;
  recipientName: string | null;
  recipientEmail: string | null;
  expiresAt: string | null;
  isActive: boolean;
  viewCount: number;
  downloadCount: number;
  createdAt: string;
}

export interface SentStatus {
  id: string;
  recipientTenantName: string;
  status: string;
  viewCount: number;
  downloadCount: number;
  createdAt: string;
}

export function getConnectedResellers() {
  return request<{ resellers: ConnectedReseller[] }>('/sharing/connected-resellers');
}

export function sendQuoteToReseller(data: {
  quoteId: string;
  recipientTenantId: string;
  projectReference?: string;
  notes?: string;
}) {
  return request<{ shared: Record<string, unknown> }>('/sharing/send', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export function getSentStatus(quoteId: string) {
  return request<{ recipients: SentStatus[] }>(`/sharing/sent-status/${quoteId}`);
}

export function listIncomingQuotes() {
  return request<{ incomingQuotes: IncomingQuoteListItem[] }>('/sharing/incoming');
}

export function getIncomingQuote(id: string) {
  return request<IncomingQuoteDetail>(`/sharing/incoming/${id}`);
}

export function createShareLink(data: {
  quoteId: string;
  recipientName?: string;
  recipientEmail?: string;
  expiresInDays?: number;
}) {
  return request<{ link: ShareLink; url: string }>('/sharing/create-link', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export function getShareLinks(quoteId: string) {
  return request<{ links: ShareLink[] }>(`/sharing/links/${quoteId}`);
}

// ── Attachments ──────────────────────────────────────────────────────────────

export interface QuoteAttachment {
  id: string;
  quoteId: string;
  filename: string;
  originalFilename: string;
  fileSize: number;
  mimeType: string;
  label: string | null;
  uploadedAt: string;
}

export function getAttachments(quoteId: string) {
  return request<{ attachments: QuoteAttachment[] }>(`/sharing/attachments/${quoteId}`);
}

export async function uploadAttachment(
  quoteId: string,
  file: File,
  label?: string,
): Promise<{ attachment: QuoteAttachment }> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('quoteId', quoteId);
  if (label) formData.append('label', label);

  const token = localStorage.getItem('token');
  const response = await fetch('/api/sharing/attachments/upload', {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new ApiError(err.error || 'Upload failed', response.status);
  }

  return response.json();
}

export function deleteAttachment(attachmentId: string) {
  return request<{ success: boolean }>(`/sharing/attachments/delete/${attachmentId}`, {
    method: 'DELETE',
  });
}

export function getAttachmentDownloadUrl(attachmentId: string): string {
  return `/api/sharing/attachments/download/${attachmentId}`;
}

// ── Collateral Library ────────────────────────────────────────────────────────

export interface CollateralItem {
  id: string;
  tenantId: string;
  filename: string;
  originalFilename: string;
  fileSize: number;
  mimeType: string;
  label: string | null;
  description: string | null;
  category: string | null;
  isDefault: boolean;
  uploadedAt: string;
  updatedAt: string;
}

export function getCollateralItems() {
  return request<{ items: CollateralItem[] }>('/collateral');
}

export async function uploadCollateralItem(file: File): Promise<CollateralItem> {
  const formData = new FormData();
  formData.append('file', file);
  const token = localStorage.getItem('token');
  const res = await fetch('/api/collateral/upload', {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  });
  if (!res.ok) throw new ApiError('Upload failed', res.status);
  return res.json();
}

export function updateCollateralItem(
  id: string,
  data: { label?: string; description?: string; category?: string; isDefault?: boolean },
) {
  return request<CollateralItem>(`/collateral/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export function deleteCollateralItem(id: string) {
  return request<{ success: boolean }>(`/collateral/${id}`, { method: 'DELETE' });
}

// ── Email Quote ───────────────────────────────────────────────────────────────

export interface EmailQuoteInput {
  quoteId: string;
  recipientEmail: string;
  recipientName?: string;
  subject?: string;
  personalMessage?: string;
  includePdf: boolean;
  collateralItemIds: string[];
}

export interface EmailQuoteResult {
  success: boolean;
  emailId?: string;
  shareUrl: string;
  shareToken: string;
}

export function emailQuote(input: EmailQuoteInput) {
  return request<EmailQuoteResult>('/sharing/email-quote', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function smsQuote(data: { quoteId: string; recipientPhone: string }) {
  return request<{ success: boolean; shareUrl?: string; shareToken?: string; error?: string }>(
    '/sharing/sms-quote',
    { method: 'POST', body: JSON.stringify(data) },
  );
}

export interface QuoteEmail {
  id: string;
  recipientEmail: string;
  recipientName: string | null;
  subject: string;
  status: string;
  sentAt: string;
}

export function getQuoteEmailLog(quoteId: string) {
  return request<{ emails: QuoteEmail[] }>(`/sharing/email-log/${quoteId}`);
}

// ── Quote Collateral ──────────────────────────────────────────────────────────

export interface QuoteCollateralItem {
  id: string;
  collateralItemId: string;
  label: string | null;
  originalFilename: string;
  fileSize: number;
  category: string | null;
}

export function getQuoteCollateral(quoteId: string) {
  return request<{ items: QuoteCollateralItem[] }>(`/sharing/quote-collateral/${quoteId}`);
}

export function setQuoteCollateral(quoteId: string, collateralItemIds: string[]) {
  return request<{ success: boolean; count: number }>(`/sharing/quote-collateral/${quoteId}`, {
    method: 'PUT',
    body: JSON.stringify({ collateralItemIds }),
  });
}

// ── Admin — Pending Associations ──────────────────────────────────────────────

export interface PendingAssociation {
  id: string;
  requesterUserEmail: string;
  requesterUserPhone: string | null;
  quoteId: string;
  quoteDisplayId: string | null;
  sourceTenantName: string;
  matchType: string;
  matchValue: string;
  status: string;
  createdAt: string;
}

export function listPendingAssociations() {
  return request<PendingAssociation[]>('/admin/pending-associations');
}

export function approvePendingAssociation(id: string) {
  return request<{ ok: boolean }>(`/admin/pending-associations/${id}/approve`, { method: 'POST' });
}

export function rejectPendingAssociation(id: string) {
  return request<{ ok: boolean }>(`/admin/pending-associations/${id}/reject`, { method: 'POST' });
}
